import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/shared/Button';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { simpleDateAtTime } from 'lib/date';
import Workflow, { ActionStatus, WorkflowAction, WorkflowCategoryOptions } from 'models/Workflow';
import { WorkflowActionIntent } from 'services/api/workflows';
import { BodySmall } from 'styles/typography';
import ChevronRight from 'svg/ChevronRight';
import WarnIcon from 'svg/WarnIcon';

import ActionCard from './categoryCard/ActionCard';
import { actionCardFor } from './categoryCard/actionCards';
import { ActionHandlers } from './categoryCard/handlers';
import {
  ActionRequired,
  ActionRow,
  BannerActions,
  BannerTitle,
  Card,
  Caret,
  CategoryTitle,
  ChevronCell,
  Choices,
  DiffList,
  DiffName,
  DiffNew,
  DiffOld,
  EligibilityLink,
  EligibilityTimestamp,
  ErroredNote,
  FlaggedCopy,
  HeaderButton,
  HeaderLeft,
  IconCell,
  InlineLink,
  Muted,
  NestedControls,
  NoticeBanner,
  Result,
  ResultLabel,
  ResultLink,
  ResultValue,
  RowButton,
  RowMain,
  Rows,
  RowTitle,
  SingleBody,
  SkippedAside,
  StatusOnly,
  StepLink,
  Subtitle,
  SummaryName,
  SummaryRow,
  SummaryValue,
} from './categoryCard/styles';
import ClinicalReviewPanel from './clinicalReview/ClinicalReviewPanel';
import {
  BENEFITS_ELIGIBILITY_KIND,
  benefitsCheckStale,
  eligibilityActions,
  EligibilityState,
  PAYER_SEARCH_KIND,
  readEligibilities,
  readEligibility,
} from './eligibility/eligibilityData';
import { actionKindMeta, conjoin, labelForValue } from './actionConfig';
import { collectedFacilityLabel } from './constants';
import FacilityDecisionPanel from './FacilityDecisionPanel';
import FanoutPanel, { buildingTypeNoun, fanoutCounts } from './FanoutPanel';
import { ReferralModalSection } from './ReferralModal';
import { categoryStatus, concludedByDenial, isQuietlyReady } from './referralProgress';
import { ActionStatusBadge, actionStatusDisplay, Pill } from './StatusBadge';
import StatusIcon, {
  indicatorColor,
  isEmphasized,
  rollupIndicator,
  StatusIndicator,
  statusIndicator,
} from './StatusIcon';
import { WarningBanner } from './WarningBanner';

// The Payer Search + Insurance Verification rows delegate their detail to the eligibility
// slideout, so the card shows only a one-line summary + a click-through.
export const ELIGIBILITY_KINDS = new Set([PAYER_SEARCH_KIND, BENEFITS_ELIGIBILITY_KIND]);

const ELIGIBILITY_SUMMARY: Record<EligibilityState, string> = {
  'select-payer': 'Select the patient’s payer to run the eligibility check',
  running: 'Running eligibility check…',
  'active-coverage': 'Active coverage found for this patient',
  'no-active-coverage': 'No active coverage found matching this patient’s provided insurance details',
  'payer-not-enrolled': 'This patient’s payer is not enrolled in Olio’s automated eligibility check',
  'no-results': 'No active coverage could be found matching this patient’s provided insurance details',
  error: 'Something went wrong while checking eligibility',
  // The slideout names the specific fields (it can read the action's unobtainableValues); the row
  // only has to say the check is waiting on someone rather than on the machine — which is the one
  // thing the empty `pending` copy it used to fall through to never said (OLIO-3330).
  blocked: 'More information is needed before the eligibility check can run',
  canceled: '',
  pending: '',
};

// The single label + status shown for the collapsed eligibility row. Payer Search and Insurance
// Verification are two backend actions but one thing to the user, so they render as one row whose
// icon + status word derive from the *combined* eligibility state (not either action's raw status):
// e.g. "no active coverage" reads Flagged/orange even though BenefitsEligibility is technically
// completed. (OLIO-2301 — collapse per UX.)
export const ELIGIBILITY_LABEL = 'Benefit Verification';

// The word shown beside the collapsed eligibility row's icon, keyed on the rolled-up indicator —
// kept word-for-word in step with ACTION_STATUS_DISPLAY (StatusBadge.tsx) so the same status
// never reads as two different words depending on which row happens to show it.
const ELIGIBILITY_ROW_LABEL: Record<StatusIndicator, string> = {
  complete: 'Complete',
  running: 'Processing',
  review: 'Ready for Review',
  attention: 'Flagged',
  error: 'Error',
  pending: 'Pending',
  muted: 'Not Applicable',
  // Never produced for an eligibility row (the indicator is set by the rail for two other
  // categories, OLIO-3170); listed so the record stays total.
  denied: 'Denied',
};

// The collapsed eligibility row's icon + word. Rolled up from the pair's API-derived effectiveStatus
// via the SAME fold the category badge and rail use (rollupIndicator) — so the row, the rail child,
// and the category pill can never disagree, and there's one source of truth for the status color.
// deriveEligibilityState still owns the row's *summary sentence* and which slideout view renders — a
// single color can't distinguish e.g. no-active-coverage vs no-results vs payer-not-enrolled.
// Takes every eligibility action the row speaks for — with two coverages that's up to four, and
// the fold is what makes "anything needing attention wins" true across them (OLIO-2860).
export function eligibilityRowDisplay(actions: (WorkflowAction | undefined)[]): {
  indicator: StatusIndicator;
  label: string;
} {
  const present = actions.filter((a): a is WorkflowAction => !!a);
  const indicator = rollupIndicator(present);
  // 'muted' is two at-rest cases one color can't split: a canceled run's pair reads
  // "Canceled" (OLIO-2729), a skipped pair reads "Not Applicable" (OLIO-3270).
  const canceled = indicator === 'muted' && present.some((action) => action.status === ActionStatus.CANCELED);
  return { indicator, label: canceled ? 'Canceled' : ELIGIBILITY_ROW_LABEL[indicator] };
}

// Stable DOM ids so "waiting on" links can scroll to the blocking step.
export const categoryAnchor = (key: string) => `ir-category-${key}`;

interface Props extends ActionHandlers {
  category: WorkflowCategoryOptions;
  actions: WorkflowAction[];
  workflow: Workflow;
}

function actionLabel(action: WorkflowAction): string {
  return action.displayLabel;
}

// "4 SNFs" — the building count captioned with the buildings' shared type. The type noun comes
// from the same all-agree helper the panel body heading uses (buildingTypeNoun), so a mixed-type
// set reads "facilities" in both places rather than the header disagreeing with the body.
function buildingCount(actions: WorkflowAction[]): string {
  return `${actions.length} ${buildingTypeNoun(actions, 'facilities')}`;
}

// Whether ActionBody renders anything for this action — banner, options/form, result, or a
// waiting reason. Terminal/in-flight states (canceled, skipped, running, completed with
// no result) have none, so a single-action category falls back to a status line.
// Every per-kind term is declared in actionConfig rather than compared against a kind name here:
// a bare `action.kind === 'DenialCheck'` fails silently on a typo or an API-side rename, and it put
// per-kind knowledge in a component that renders every kind.
function hasActionDetail(action: WorkflowAction, workflow: Workflow): boolean {
  const meta = actionKindMeta(action.kind);
  if (action.stale || action.isFlagged || action.isPending) return true;
  // Completes with a surface of its own even without a recorded result value to render.
  if (action.isCompleted && meta?.detailWhenCompleted) return true;
  // Actionable via option buttons, or via a bespoke form/panel instead of them.
  if (action.isActionRequired && (!!meta?.options || meta?.bodyWhenActionRequired)) return true;
  // Every errored action renders a body, so this does not turn on a per-kind flag: either the kind
  // owns one (`detailWhenErrored`) or it gets the generic failed-check sentence in ActionBody.
  if (action.isErrored) return true;
  // In flight, for a kind that reaches an external system and can say what it is doing.
  if (action.isRunning && meta?.detailWhenRunning) return true;
  // A headless run in flight owns body copy ("running automatically…", OLIO-2888);
  // attended running stays the status-only row.
  if (action.isRunning && meta?.detailWhenRunningHeadless && workflow.retrievalMode === 'headless') return true;
  return action.isCompleted && (!!meta?.resultValueName || !!meta?.resultSummaryValues);
}

// The category's headline status is a roll-up of its actions (design §12): a person
// needed anywhere wins, then done-when-all-done, else still going.

// One rendered row: either a normal per-action row, or the single collapsed eligibility row
// standing in for the Payer Search + Insurance Verification pair.
export type RowItem = { kind: 'action'; action: WorkflowAction } | { kind: 'eligibility' };

// Collapse the two eligibility actions into one row (at the position of the first), leaving every
// other action as its own row. Handles either eligibility action being absent.
export function buildRowItems(actions: WorkflowAction[]): RowItem[] {
  const items: RowItem[] = [];
  let eligibilityAdded = false;
  for (const action of actions) {
    if (ELIGIBILITY_KINDS.has(action.kind)) {
      if (!eligibilityAdded) {
        items.push({ kind: 'eligibility' });
        eligibilityAdded = true;
      }
      continue;
    }
    items.push({ kind: 'action', action });
  }
  return items;
}

// The states where a person owes a step something. Mirrors the API's
// Workflow::Evaluator::HUMAN_NEEDED_STATUSES, which is the same set the engine uses to decide
// whether the RUN needs attention — so a row hidden here can never be the unexplained cause of a
// flagged referral.
const HUMAN_NEEDED = new Set<ActionStatus>([ActionStatus.ACTION_REQUIRED, ActionStatus.FLAGGED, ActionStatus.ERRORED]);

// True when a row should be left out: its kind renders only when a person owes it something, and
// right now nobody does (OLIO-3158). Named for what it returns, since the question at the call
// site is "hide this?" — the kind-level policy it reads is actionConfig's renderOnlyWhenHumanNeeded.
//
// Completed counts as worth showing (OLIO-3302). Confirm Referral Source can rest there now, and it
// only gets there by a person answering it, so hiding it would take a decision someone just made
// straight back off the screen. What stays hidden is the quiet park: a source that matched one of
// the client's own names on its own, which is most referrals and was never a question.
export function hiddenWhileQuiet(action: WorkflowAction): boolean {
  const meta = actionKindMeta(action.kind);
  // A purely derived step stays hidden through completion too — completing IS its quiet state. It
  // still surfaces the moment it needs a person, so the flag can never swallow a row someone owes
  // something to.
  //
  // Checked BEFORE the completed exemption below, and that order is the whole point of the flag
  // being separate (OLIO-3330 x OLIO-3302): the exemption exists so an answer a person just gave
  // isn't taken back off the screen, which is right for Confirm Referral Source and wrong for a
  // step no one can act on. A kind that somehow set both flags means "never show me", so
  // purelyDerived has to win.
  if (meta?.purelyDerived) return !HUMAN_NEEDED.has(action.effectiveStatus);
  if (!meta?.renderOnlyWhenHumanNeeded) return false;
  if (action.effectiveStatus === ActionStatus.COMPLETED) return false;

  return !HUMAN_NEEDED.has(action.effectiveStatus);
}

export default function CategoryCard({ category, actions, workflow, ...handlers }: Props) {
  // A fanned-out kind (every instance building-stamped) collapses into one panel
  // (OLIO-2611); anything else renders as before. Categories can mix both.
  const byKind = new Map<string, WorkflowAction[]>();
  actions.forEach((action) => byKind.set(action.kind, [...(byKind.get(action.kind) ?? []), action]));
  const grouped = Array.from(byKind.entries()).filter(([, siblings]) =>
    siblings.every((sibling) => sibling.groupId !== null)
  );
  // A group-stamped kind that still reads as ONE row (actionConfig's collapseFanout — the EHR sync).
  // Its siblings are dropped except the representative, which then renders as an ordinary row.
  const collapsed = grouped.filter(([kind]) => actionKindMeta(kind)?.collapseFanout);
  const fannedKinds = grouped.filter(([kind]) => !actionKindMeta(kind)?.collapseFanout);
  const representatives = new Set(
    collapsed.map(([, siblings]) => representativeFor(siblings, workflow)?.id).filter(Boolean) as string[]
  );
  const hiddenIds = new Set(
    grouped.flatMap(([, siblings]) => siblings.map((sibling) => sibling.id)).filter((id) => !representatives.has(id))
  );
  const plain = actions.filter((action) => !hiddenIds.has(action.id) && !hiddenWhileQuiet(action));

  // "One action, unfanned" — so a hidden row doesn't keep a genuinely single-action category out of
  // this branch, while a collapsed fan-out still isn't single (it renders one row but is several
  // actions, and its subtitle carries the building count). Not `plain`, which already folds the
  // fan-out siblings into their representative and would call a 3-building sync single.
  //
  // Every branch this guards reads `plain[0]`, not `actions[0]`: with a parked step in the
  // category, index 0 of the raw list can be the row nobody is meant to see.
  const visible = actions.filter((action) => !hiddenWhileQuiet(action));
  const single = visible.length === 1 && fannedKinds.length === 0;
  // Collapse the eligibility pair (Payer Search + Insurance Verification) among the plain,
  // non-fanned rows into one row (OLIO-2301).
  const rowItems = buildRowItems(plain);
  // Active or needs-a-person — running, ready-for-review, flagged, stale, or errored. Keyed on
  // effectiveStatus so an API-derived flag (a no-active-coverage eligibility result or a DenialCheck
  // match, both technically completed) counts, and an errored facility counts too so its Retry is
  // reachable (mirrors categoryStatus(), which ranks ERRORED as needs-attention above done).
  //
  // Over `visible`, not the raw list: a row hidden while it is quiet must not open the card for
  // something nobody can see (OLIO-3158).
  const needsAttention = visible.some(
    (a) =>
      a.effectiveStatus === ActionStatus.ACTION_REQUIRED ||
      a.effectiveStatus === ActionStatus.FLAGGED ||
      a.stale ||
      a.effectiveStatus === ActionStatus.RUNNING ||
      a.effectiveStatus === ActionStatus.ERRORED
  );
  /**
   * A mention email deep-links to one action (`?actionId=`), and the drawer that opens for it
   * lives inside this card's body — which only renders while expanded. A review that has already
   * been read presents as `completed`, so `needsAttention` is false and the card starts collapsed:
   * exactly the case a note gets discussed in. Without this the link lands on the referral and
   * opens nothing.
   */
  const [searchParams] = useSearchParams();
  const holdsDeepLink = actions.some((a) => a.id === searchParams.get('actionId'));
  const [expanded, setExpanded] = useState(needsAttention || holdsDeepLink);
  // These per-facility assessments settle in seconds while the coordinator watches. Expansion is
  // seeded once above, then re-opened whenever the category NEWLY needs attention after mount
  // (e.g. a facility errors post-mount, turning the pill red) so the body — and the Retry on a
  // failed facility — is reachable without a manual click. Only opens, never force-closes, so a
  // deliberate collapse of an already-attention card is respected.
  const wasAttention = useRef(needsAttention);
  useEffect(() => {
    if (needsAttention && !wasAttention.current) setExpanded(true);
    wasAttention.current = needsAttention;
  }, [needsAttention]);
  // Which slideout-backed row (currently DenialCheck) has its detail slideout open.
  const [openSlideoutId, setOpenSlideoutId] = useState<string | null>(null);

  const title = single ? actionLabel(plain[0]) : category.name;
  const onlyFanned = fannedKinds.length === 1 && plain.length === 0;
  // A lone fanned kind captions the header: Facility Decisions gets the result rollup
  // ("2 Accepted • 1 Denied"); other per-building kinds get the plain building count
  // ("4 SNFs", per the design). Otherwise list the rows, with the eligibility pair collapsed
  // to one "Insurance Eligibility Check" label.
  const subtitle = single
    ? null
    : onlyFanned
      ? fannedKinds[0][0] === 'FacilityDecision'
        ? fanoutCounts(workflow, fannedKinds[0][1], actionKindMeta('FacilityDecision'))
        : // A lone building needs no count caption ("1 SNF" is noise) — show it only for 2+.
          fannedKinds[0][1].length > 1
          ? buildingCount(fannedKinds[0][1])
          : null
      : // `rowItems`, not `buildRowItems(actions)`: the raw list still holds every per-building
        // instance of a collapsed kind, so a 3-building referral captioned the card
        // "Sync Patient to EHR • Sync Patient to EHR • Sync Patient to EHR" while the body below it
        // correctly showed one row. The caption has to describe the rows a coordinator can actually
        // see, which is what rowItems already is.
        rowItems
          .map((item) => (item.kind === 'eligibility' ? ELIGIBILITY_LABEL : actionLabel(item.action)))
          .join(' • ');

  // Quiet-ready single step reads Ready for Review; otherwise the category roll-up. Each eligibility
  // action folds in via its API-derived effectiveStatus (categoryStatus reads it), so the pill agrees
  // with the collapsed eligibility row without any eligibility-specific logic here.
  // Over `visible` for the same reason the rows are: a hidden row must not vote on the badge, or
  // a parked resolve step reads Pending on a category whose every visible row is Completed.
  const badgeStatus = single && isQuietlyReady(plain[0]) ? ActionStatus.ACTION_REQUIRED : categoryStatus(visible);
  // A denial concludes this category (OLIO-3170): the rows are all complete, so the rollup would say
  // green "Complete", which is true of the work and wrong about the answer. Red Denied instead, and
  // the header tints to match — the same predicate the rail uses for its icon.
  const denied = concludedByDenial(workflow, actions);

  return (
    <Card id={categoryAnchor(category.key)} data-testid={`category-card-${category.key}`}>
      <HeaderButton
        type='button'
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        $open={expanded}
        $tone={denied ? 'danger' : actionStatusDisplay(badgeStatus).tone}>
        <HeaderLeft>
          <Caret $open={expanded}>
            <ChevronRight width={12} height={12} />
          </Caret>
          <div>
            <CategoryTitle>{title}</CategoryTitle>
            {subtitle && <Subtitle data-testid='category-subtitle'>{subtitle}</Subtitle>}
          </div>
        </HeaderLeft>
        {denied ? (
          <Pill $tone='danger' data-testid='action-status'>
            Denied
          </Pill>
        ) : (
          <ActionStatusBadge status={badgeStatus} />
        )}
      </HeaderButton>

      {expanded &&
        (single ? (
          <SingleBody>
            {hasActionDetail(plain[0], workflow) ? (
              <ActionBody action={plain[0]} workflow={workflow} {...handlers} />
            ) : (
              // No detail to show (e.g. canceled / running) — surface the status so the
              // body isn't empty, consistent with the rows in multi-action categories.
              <StatusOnly>
                <StatusIcon indicator={statusIndicator(plain[0])} size={16} />
                <RowTitle
                  $color={indicatorColor(statusIndicator(plain[0]))}
                  $bold={isEmphasized(statusIndicator(plain[0]))}>
                  {actionStatusDisplay(plain[0].effectiveStatus).label}
                </RowTitle>
              </StatusOnly>
            )}
          </SingleBody>
        ) : (
          <>
            {fannedKinds.map(([kind, siblings]) =>
              kind === 'FacilityDecision' ? (
                <FacilityDecisionPanel
                  key={kind}
                  actions={siblings}
                  workflow={workflow}
                  onComplete={handlers.onCompleteBatch}
                  onAmend={handlers.onAmend}
                  onInvoke={handlers.onInvoke}
                  isCompleting={handlers.isCompleting || handlers.isInvoking}
                  // The panel's rows own the Accept/Deny buttons and show the decision
                  // as the row subtitle (per the Figma row layout) — ActionBody only
                  // contributes the banners/waiting states here.
                  renderRow={(action) => <ActionBody action={action} workflow={workflow} {...handlers} hideResult />}
                />
              ) : kind === 'ClinicalReview' ? (
                <ClinicalReviewPanel
                  key={kind}
                  actions={siblings}
                  workflow={workflow}
                  onInvoke={handlers.onInvoke}
                  isInvoking={handlers.isInvoking}
                  onView={handlers.onView}
                />
              ) : (
                <FanoutPanel
                  key={kind}
                  actions={siblings}
                  blurb={siblings[0].description}
                  renderRow={(action) => <ActionBody action={action} workflow={workflow} {...handlers} />}
                />
              )
            )}
            {plain.length > 0 && (
              <Rows>
                {rowItems.map((item) => {
                  // The collapsed eligibility row stands in for the Payer Search + Insurance
                  // Verification pair among the plain rows.
                  if (item.kind === 'eligibility') {
                    return (
                      <EligibilityRow
                        key='eligibility'
                        workflow={workflow}
                        onOpenEligibility={handlers.onOpenEligibility}
                        onInvoke={handlers.onInvoke}
                        isInvoking={handlers.isInvoking}
                      />
                    );
                  }
                  if (item.action.status === ActionStatus.SKIPPED) {
                    return <SkippedRow key={item.action.id} action={item.action} onEdit={handlers.onEdit} />;
                  }
                  const action = item.action;
                  return (
                    <ActionCard
                      key={action.id}
                      action={action}
                      workflow={workflow}
                      body={<ActionBody action={action} workflow={workflow} {...handlers} />}
                      slideoutOpen={openSlideoutId === action.id}
                      onOpenSlideout={() => setOpenSlideoutId(action.id)}
                      onCloseSlideout={() => setOpenSlideoutId(null)}
                      handlers={handlers}
                    />
                  );
                })}
              </Rows>
            )}
          </>
        ))}
    </Card>
  );
}

// The per-action content: its description, plus the controls/result/wait reason
// that depend on status. Reused for single- and multi-action categories.
function ActionBody(props: { action: WorkflowAction; workflow: Workflow; hideResult?: boolean } & ActionHandlers) {
  const {
    action,
    workflow,
    onComplete,
    isCompleting,
    onInvoke,
    isInvoking,
    onEdit,
    onOpenEligibility,
    hideResult = false,
  } = props;
  const meta = actionKindMeta(action.kind);

  // Payer Search + Insurance Verification delegate their detail (payer select, results, errors)
  // to the eligibility slideout: show a one-line summary + a click-through, or the waiting reason
  // while still pending. The generic options/result blocks below never apply to these kinds.
  if (ELIGIBILITY_KINDS.has(action.kind)) {
    return (
      <>
        {action.stale && (
          <StaleBanner action={action} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />
        )}
        {action.isPending ? (
          <WaitingOn action={action} workflow={workflow} />
        ) : (
          <EligibilitySummaryLink
            action={action}
            workflow={workflow}
            onOpen={() => onOpenEligibility(action.facetId)}
          />
        )}
      </>
    );
  }

  // The generic renderers, handed to the kind as nodes. A kind's body composes them around its
  // own panel, so "replace the generic surface" and "render alongside it" are the same mechanism
  // and neither needs a schema flag. Each is null in the states it does not apply to, which is
  // what lets one ordering serve both the completed and the action_required cases.
  const genericResult =
    action.isCompleted && !hideResult ? <CompletedResult action={action} workflow={workflow} /> : null;

  const genericActions = (
    <>
      {action.isActionRequired && meta?.options && (
        <ActionRequired>
          {meta.prompt && <BodySmall>{meta.prompt}</BodySmall>}
          <Choices>
            {meta.options.map((option) => (
              <Button
                key={option.label}
                variant={option.tone === 'danger' ? 'outline-danger' : 'primary'}
                size='small'
                disabled={isCompleting}
                onClick={() => onComplete(action.id, option.outputs)}
                data-testid={`action-option-${option.label}`}>
                {option.label}
              </Button>
            ))}
          </Choices>
        </ActionRequired>
      )}

      {action.isPending &&
        // The design explains a waiting step with its own description; the
        // blocker-linking WaitingOn is the fallback for steps without one.
        (action.description ? <Muted>{action.description}</Muted> : <WaitingOn action={action} workflow={workflow} />)}
    </>
  );

  const ownBody = actionCardFor(action).body;

  return (
    <>
      {action.stale && <StaleBanner action={action} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />}

      {action.isErrored && !meta?.detailWhenErrored && (
        <ErroredPrompt action={action} onInvoke={onInvoke} isInvoking={isInvoking} />
      )}

      {action.isFlagged && <FlaggedPrompt action={action} workflow={workflow} onEdit={onEdit} />}

      {/* Ahead of the body rather than between the result and the actions, as it was. Safe because
          a step is only ever in one status, so this and the result never render together. */}
      {action.isActionRequired && <MidRedoNotice action={action} workflow={workflow} />}

      {ownBody ? (
        ownBody({ action, workflow, handlers: props, hideResult, genericResult, genericActions })
      ) : (
        <>
          {genericResult}
          {genericActions}
        </>
      )}
    </>
  );
}

// The failed-run body, generic for every kind that does not own an errored surface of its own.
// The design shows the identical body on each failed auto-check, so this is not per-kind knowledge.
//
// `detailWhenErrored` marks the kinds that DO own one, and they are excluded for two reasons: their
// body already states the failure — rendering both stacks two error sentences and two controls on
// one row — and their recovery is not this write. SyncPatientToEhr's Retry is `rerun` THEN
// `dispatch`, because `errored` is not in the API's DISPATCHABLE_FROM; the generic link sends only
// the `rerun` half, so it would reset the instance without sending anything while looking like it
// had worked.
//
// Keyed on `status`, not `effectiveStatus`: offering a re-run is behavior, and behavior never reads
// the presented status (WorkflowAction model). A failed-parse Process Documents step is `completed`
// here and owns its own retry in ProcessAttachmentsNote.
//
// The label renders verbatim rather than lowercased — these are proper names of checks, and
// lowercasing mangles acronyms ("sync patient to ehr"). It matches the row title above it, which
// already reads `Error: <label>`.
function ErroredPrompt({
  action,
  onInvoke,
  isInvoking,
}: {
  action: WorkflowAction;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const { canEditAction } = useWorkflowPermissions();

  return (
    <ErroredNote data-testid='action-errored-note'>
      {`There was an error running the ${actionLabel(action)}. `}
      {/* A reader still sees that it failed; only the acting link needs the grant, matching
          StaleBanner and ProcessAttachmentsNote. */}
      {canEditAction(action) && (
        <InlineLink
          type='button'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'rerun')}
          data-testid='action-errored-retry'>
          Try again
        </InlineLink>
      )}
    </ErroredNote>
  );
}

// The single collapsed row standing in for the Payer Search + Insurance Verification pair. Its
// icon + status word come from the combined eligibility state; the body is the shared one-line
// summary + click-through to the slideout (or the waiting reason while still pending). Re-run /
// dismiss for a stale eligibility action stay available via the stale banner.
function EligibilityRow({
  workflow,
  onOpenEligibility,
  onInvoke,
  isInvoking,
}: {
  workflow: Workflow;
  onOpenEligibility: (facetId: string | null) => void;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  // One row for the work however many coverages sit behind it, so the icon and word roll up over
  // all of them — anything needing attention wins (OLIO-2860).
  const contexts = readEligibilities(workflow);
  const allActions = eligibilityActions(contexts);
  const { indicator, label } = eligibilityRowDisplay(allActions);
  // The coverage the row speaks for: the first whose own state produced the rolled-up indicator,
  // so the sentence, the timestamp, and the icon can't describe different coverages.
  const lead =
    contexts.find((c) => eligibilityRowDisplay([c.payerSearch, c.benefits]).indicator === indicator) ?? contexts[0];
  const { state, payerSearch, benefits } = lead;
  // The stale banner has to come from the LEAD coverage, not from any coverage. Both `flagged` and
  // `stale` roll up to the same indicator, so one coverage flagged and a different one stale both
  // produce `attention` — reading staleness across all of them would put a re-run button for one
  // coverage next to a sentence about another. A stale coverage that isn't the lead gets its own
  // re-run on its own tab (OLIO-2794); it doesn't belong on a row describing someone else.
  const staleAction = [lead.payerSearch, lead.benefits].find((action) => action?.stale);
  const pendingAction = benefits ?? payerSearch;
  const lastRun = benefits?.completedAt;
  const dateStale = benefitsCheckStale(benefits);
  // Waiting on an input, or a run that was canceled — nothing to open, so the row isn't a trigger.
  const waiting = state === 'pending' && !!pendingAction;
  const openable = !waiting && state !== 'canceled';
  const open = () => onOpenEligibility(lead.facetId);

  return (
    <ActionRow
      data-testid='eligibility-action-row'
      data-action-kind='InsuranceEligibilityCheck'
      $flagged={indicator === 'attention'}
      $clickable={openable}
      onClick={openable ? open : undefined}>
      <IconCell>
        <StatusIcon indicator={indicator} size={16} />
      </IconCell>
      <RowMain>
        <RowTitle $color={indicatorColor(indicator)} $bold={isEmphasized(indicator)}>
          {label}: {ELIGIBILITY_LABEL}
        </RowTitle>
        {/* The banner's own Re-run / Dismiss are the whole point of it — a click on either must not
            also open the slideout behind it. */}
        {staleAction && (
          <NestedControls onClick={(e) => e.stopPropagation()}>
            <StaleBanner action={staleAction} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />
          </NestedControls>
        )}
        {waiting ? (
          // Same rule the per-action rows follow (see the description/WaitingOn pair above): a
          // waiting step is explained by its own description, and the blocker-linking fallback is
          // for steps that have none. This row is collapsed from two actions and reads as ONE step
          // to a coordinator, so it wants the one sentence saying what the step is for — not a
          // pointer at the plumbing behind it, which named an upstream step the reader never asked
          // about (OLIO-3383).
          pendingAction!.description ? (
            <Muted>{pendingAction!.description}</Muted>
          ) : (
            <WaitingOn action={pendingAction!} workflow={workflow} />
          )
        ) : (
          openable && (
            // The row is the click target now (matching every other slideout-backed row), but this
            // stays a button so the step is still reachable by keyboard. Its click would otherwise
            // bubble to the row and open the same slideout twice.
            <EligibilityLink
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
              data-testid='open-eligibility'>
              <span>{ELIGIBILITY_SUMMARY[state] || 'View eligibility details'}</span>
            </EligibilityLink>
          )
        )}
        {lastRun && (
          <EligibilityTimestamp $stale={dateStale} data-testid='eligibility-last-run'>
            {dateStale && <WarnIcon width={11} height={11} />}
            <span>
              Check last completed: {simpleDateAtTime(lastRun)}
              {dateStale && ' — may be out of date'}
            </span>
          </EligibilityTimestamp>
        )}
      </RowMain>
      {openable && (
        <ChevronCell>
          <ChevronRight width={14} height={14} />
        </ChevronCell>
      )}
    </ActionRow>
  );
}

// The one instance a collapsed per-building kind speaks for.
//
// THE SELECTED FACILITY'S, not the first and not the first non-skipped. Those agree almost always and
// diverge in the case that matters: after a push lands at A and the coordinator moves the winner to B,
// A's instance holds the landed result while B's is the one that must be sent. Picking by position
// would render A's "already synced" card while the actual work sits on B — a wrong-building card at
// exactly the moment someone is moving a real patient.
//
// Pre-decision there is no winner and every sibling is `pending` (the card renders the definition's
// description either way), so any stable pick will do; sorting by id keeps it from reshuffling between
// refetches.
function representativeFor(siblings: WorkflowAction[], workflow: Workflow): WorkflowAction | undefined {
  const selected = workflow.valueFor('selected_facility_id')?.value;
  const winner = selected ? siblings.find((sibling) => sibling.groupId === selected) : undefined;
  return winner ?? [...siblings].sort((a, b) => a.id.localeCompare(b.id))[0];
}

// Explain which step(s) a pending action is waiting on, linkable to that step.
function WaitingOn({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const blockers = blockersFor(action, workflow);

  if (blockers.length === 0) {
    return <Muted>Waiting on earlier steps before this can start.</Muted>;
  }

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Muted data-testid='waiting-on'>
      Waiting on{' '}
      {blockers.map((blocker, i) => (
        <span key={blocker.id}>
          {i > 0 && (i < blockers.length - 1 ? ', ' : blockers.length > 2 ? ', and ' : ' and ')}
          <StepLink type='button' onClick={() => scrollTo(blocker.anchor)}>
            {blocker.label}
          </StepLink>
        </span>
      ))}
      .
    </Muted>
  );
}

// The not-yet-completed producer action(s) of each missing required value — the
// step(s) that, once done, would unblock this action.
// Payer Search and Insurance Verification are ONE step to the person reading this — they render as
// a single Benefit Verification row. So naming one as what the other is waiting on describes the
// step as waiting on itself, which is only confusing. A blocker is a step someone could go and look
// at; the coverage's own payer selection is handled inside its slideout.
function sameCollapsedStep(action: WorkflowAction, producer: WorkflowAction): boolean {
  return ELIGIBILITY_KINDS.has(action.kind) && ELIGIBILITY_KINDS.has(producer.kind);
}

// One blocking step: `id` identifies the step (so two of them are two entries), `anchor` is where
// clicking scrolls to, which is the category it sits in — several blockers can share one.
interface Blocker {
  id: string;
  anchor: string;
  label: string;
}

function blockersFor(action: WorkflowAction, workflow: Workflow): Blocker[] {
  const missing = action.required.filter((name) => !workflow.valueFor(name, action)?.value);

  // Keyed by PRODUCER, not by the category it scrolls to. One step producing two of the missing
  // values should be named once — but two different steps in the same category are two steps, and
  // keying on the shared anchor silently dropped all but the last.
  const byProducer = new Map<string, Blocker>();
  missing.forEach((name) => {
    // The producer is whichever action declares it produces this value (API-provided
    // required/produces — no hardcoded kind→value map on the FE). Lane-scoped: a
    // per-building step is only ever unblocked by its own building's producer (or a
    // shared one) — never a sibling building's.
    const producer = workflow.actions.find(
      (a) => a.produces.includes(name) && (a.groupId === null || a.groupId === action.groupId)
    );
    if (producer && !producer.isCompleted && !sameCollapsedStep(action, producer)) {
      byProducer.set(producer.id, {
        id: producer.id,
        anchor: categoryAnchor(producer.categoryKey ?? ''),
        label: producer.displayLabel,
      });
    }
  });

  return Array.from(byProducer.values());
}

function StaleBanner({
  action,
  workflow,
  onInvoke,
  isInvoking,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const diff = workflow.inputDiffFor(action);
  const { canEditAction } = useWorkflowPermissions();

  return (
    <WarningBanner data-testid='stale-banner'>
      <BannerTitle>Inputs changed since this ran</BannerTitle>
      {diff.length > 0 ? (
        <DiffList>
          {diff.map((d) => (
            <li key={d.name}>
              <DiffName>{labelForValue(workflow, d.name)}</DiffName>: <DiffOld>{d.ranWith || '—'}</DiffOld> →{' '}
              <DiffNew>{d.current || '—'}</DiffNew>
            </li>
          ))}
        </DiffList>
      ) : (
        // Trust the `stale` flag even when we can't enumerate the changed inputs
        // (empty snapshot, or a value the FE can't resolve) — don't over-claim.
        <Muted>Its inputs have changed since it ran.</Muted>
      )}
      {/* A reader still sees that the inputs changed; only the acting buttons need the grant. */}
      {canEditAction(action) && (
        <BannerActions>
          <Button
            size='small'
            disabled={isInvoking}
            onClick={() => onInvoke(action.id, 'rerun')}
            data-testid='stale-rerun'>
            Re-run
          </Button>
          <Button
            variant='outline-gray'
            size='small'
            disabled={isInvoking}
            onClick={() => onInvoke(action.id, 'dismiss')}
            data-testid='stale-dismiss'>
            Dismiss
          </Button>
        </BannerActions>
      )}
    </WarningBanner>
  );
}

// The mid-redo awareness notice (design §9/§13): a producer of this step's inputs is
// in flight, so the information this step relies on may be about to change. Awareness,
// never a block — the person can still act (the engine never gates the decision).
function MidRedoNotice({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const producers = workflow.inFlightProducersFor(action);
  if (producers.length === 0) return null;

  return (
    <NoticeBanner data-testid='mid-redo-notice'>
      {conjoin(producers.map((producer) => producer.displayLabel))} {producers.length > 1 ? 'are' : 'is'} being redone
      and may change the information this step relies on. You can still act now.
    </NoticeBanner>
  );
}

// A flagged step can't run until a person supplies what the engine couldn't derive.
// Per the design it reads as instructional copy with the fix inline ("Add documents",
// "Manually add demographics") — no alarm box; the orange row title carries the state.
function FlaggedPrompt({
  action,
  workflow,
  onEdit,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onEdit?: (section: ReferralModalSection) => void;
}) {
  const meta = actionKindMeta(action.kind);
  // The flag isn't a failure when nothing is actually missing downstream. A produced
  // value counts as outstanding only if it's absent AND some live step still requires
  // it — Extract also produces nice-to-haves (plan type, referral source) that no step
  // consumes, and their absence doesn't mean the person has anything left to add.
  // Contract-driven: `produces`/`required` come from the API.
  const liveConsumerNeeds = (name: string) =>
    !workflow.valueFor(name, action)?.value &&
    workflow.actions.some(
      (other) =>
        other.id !== action.id &&
        other.status !== ActionStatus.COMPLETED &&
        other.status !== ActionStatus.CANCELED &&
        other.status !== ActionStatus.SKIPPED &&
        other.required.includes(name) &&
        (other.groupId === null || action.groupId === null || other.groupId === action.groupId)
    );
  const outputsSatisfied = action.produces.length > 0 && !action.produces.some(liveConsumerNeeds);
  const names = action.unobtainableValues.map((name) => labelForValue(workflow, name));
  const copy =
    (outputsSatisfied && meta?.flaggedPromptSatisfied) ||
    meta?.flaggedPrompt ||
    (names.length > 0
      ? `This step needs ${conjoin(names)} to continue.`
      : 'This step needs more information before it can run.');
  // No opener means no edit permission: the copy still explains what the step is missing, but
  // the links that would fix it are not offered (OLIO-2615).
  const links = onEdit
    ? (meta?.flaggedLinks?.filter((link) => (outputsSatisfied ? !link.hideWhenSatisfied : !link.hideWhenUnsatisfied)) ??
      [])
    : [];

  return (
    <FlaggedCopy data-testid='flagged-prompt'>
      {copy}
      {links.map((link, i) => (
        <span key={link.label}>
          {i === 0 ? ' ' : ' or '}
          <InlineLink type='button' onClick={() => onEdit?.(link.section)} data-testid={`flagged-${link.section}`}>
            {link.label}
          </InlineLink>
        </span>
      ))}
    </FlaggedCopy>
  );
}

// A skipped step, Option C (OLIO-2633): the same "{Status}: {Name}" prefixed title every other
// row uses (never bold — nothing to draw attention to) plus muted copy, and — when the step can
// still be fed later — the affordance that revives it (Add Documents opens the edit modal; the
// upload re-runs the parked lane engine-side).
function SkippedRow({ action, onEdit }: { action: WorkflowAction; onEdit?: (section: ReferralModalSection) => void }) {
  const meta = actionKindMeta(action.kind);
  const indicator = statusIndicator(action);

  return (
    <ActionRow data-testid='action-row' data-action-kind={action.kind}>
      {/* The same icon cell every other row wears. Dropping the chip (Option C) took the whole
          gutter with it, so these rows sat unindented with nothing where their status dot belongs
          and the titles below them didn't line up (OLIO-3270). */}
      <IconCell>
        <StatusIcon indicator={indicator} size={16} />
      </IconCell>
      <RowMain>
        <RowTitle $color={indicatorColor(indicator)}>
          {actionStatusDisplay(action.effectiveStatus).label}: {action.displayLabel}
        </RowTitle>
        {meta?.skippedCopy && <Muted>{meta.skippedCopy}</Muted>}
      </RowMain>
      {meta?.skippedAction && onEdit && (
        <SkippedAside>
          <RowButton
            variant='outline-primary'
            onClick={() => onEdit(meta.skippedAction!.section)}
            data-testid='skipped-action'>
            {meta.skippedAction.label}
          </RowButton>
        </SkippedAside>
      )}
    </ActionRow>
  );
}

// A one-line summary of the eligibility state + a click-through to open the slideout, where the
// payer select / full result / error detail lives. Shown on the Payer Search + Insurance
// Verification rows for every non-pending state.
function EligibilitySummaryLink({
  action,
  workflow,
  onOpen,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onOpen: () => void;
}) {
  // This action's OWN coverage — the two rows of one coverage share a derived state, but a
  // sibling coverage's has nothing to do with them.
  const { state } = readEligibility(workflow, action.facetId);
  // The two rows share the derived state; while running, each row describes its own step.
  const summary =
    state === 'running' && action.kind === PAYER_SEARCH_KIND ? 'Running payer search…' : ELIGIBILITY_SUMMARY[state];

  // Never ran and never will (run canceled) — no detail to open.
  if (state === 'canceled') return null;

  return (
    <EligibilityLink type='button' onClick={onOpen} data-testid='open-eligibility'>
      <span>{summary || 'View eligibility details'}</span>
      <ChevronRight width={12} height={12} />
    </EligibilityLink>
  );
}

// Array values summarize entry by entry — documents as their filenames, collected facilities as
// what they say (or the facility they picked, resolved against `facilities`); scalars render as-is.
function formatSummaryValue(value: unknown, facilities: { id: string; name: string }[] = []): string {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return String(entry);
        const filename = (entry as { filename?: string }).filename;
        return filename ?? collectedFacilityLabel(entry, facilities);
      })
      .join(', ');
  }
  return String(value);
}

// A facility a person picked carries only its id, so its name has to be looked up. Nothing else
// a summary renders does.
function holdsPickedFacility(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.some((entry) => !!entry && typeof entry === 'object' && !!(entry as { mappedValue?: unknown }).mappedValue)
  );
}

// The looked-up rendering, in its own component so the facility query is asked for by the rows
// that actually need it — a completed card with no picked facility on it renders without
// waiting on that request at all.
function PickedFacilitySummary({ value }: { value: unknown }) {
  const { facilities } = useAllowedFacilities();
  return <>{formatSummaryValue(value, facilities)}</>;
}

function SummaryValueText({ value }: { value: unknown }) {
  if (holdsPickedFacility(value)) return <PickedFacilitySummary value={value} />;
  return <>{formatSummaryValue(value)}</>;
}

function CompletedResult({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const meta = actionKindMeta(action.kind);
  if (!meta) return null;

  // Read as unknown: integration-completed kinds (BenefitsEligibility) store a nested
  // object here, not the scalar the human-completed kinds write. Lane-aware read: a
  // per-building step's result is ITS building's value, a per-coverage step's is ITS
  // coverage's — passing the action covers both.
  const value: unknown = meta.resultValueName ? workflow.valueFor(meta.resultValueName, action)?.value : null;
  const rawLinkId = meta.resultLink ? workflow.valueFor(meta.resultLink.valueName)?.value : null;
  const linkId = typeof rawLinkId === 'string' ? rawLinkId : null;
  const summary = (meta.resultSummaryValues ?? [])
    .map((name) => ({ name, value: workflow.valueFor(name)?.value }))
    .filter((entry) => (Array.isArray(entry.value) ? entry.value.length > 0 : entry.value));
  if (value == null && summary.length === 0) return null;

  const rawLabel =
    value != null
      ? (meta.options?.find((o) => o.outputs[meta.resultValueName!] === value)?.label ??
        (typeof value === 'string' ? meta.resultLabels?.[value] : undefined) ??
        value)
      : null;
  // Defensive: only scalar labels reach JSX — never hand an unexpected object to React.
  const label = typeof rawLabel === 'string' ? rawLabel : null;

  return (
    <Result data-testid='action-result'>
      {label && (
        <>
          <ResultLabel>Result</ResultLabel>
          <ResultValue>
            {label}
            {linkId && meta.resultLink && (
              <ResultLink to={meta.resultLink.path(linkId)} data-testid='result-link'>
                {meta.resultLink.label} →
              </ResultLink>
            )}
          </ResultValue>
        </>
      )}
      {summary.map((entry) => (
        <SummaryRow key={entry.name} data-testid={`result-summary-${entry.name}`}>
          <SummaryName>{labelForValue(workflow, entry.name)}</SummaryName>
          <SummaryValue>
            <SummaryValueText value={entry.value} />
          </SummaryValue>
        </SummaryRow>
      ))}
    </Result>
  );
}
